/**
 * Naia Blanc — scroll lock (ref-count) + focus trap helpers for custom drawers.
 * Uses Horizon `html[scroll-lock]` so desktop scroll on `.page-wrapper` is blocked correctly.
 */
(function (global) {
  /** @type {Set<EventTarget>} */
  const scrollOwners = new Set();

  /** @type {WeakMap<Element, (ev: KeyboardEvent) => void>} */
  const focoHandlers = new WeakMap();

  const SELECTORES_FOCO =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function anchoBarraScroll() {
    return Math.max(0, global.innerWidth - document.documentElement.clientWidth);
  }

  function aplicarCompensacionBarra(gap) {
    const valor = gap > 0 ? `${gap}px` : '';
    document.body.style.paddingRight = valor;
    document.querySelectorAll('[data-cabecera-barra]').forEach((el) => {
      if (gap > 0) el.style.paddingRight = valor;
      else el.style.removeProperty('padding-right');
    });
  }

  function syncScrollLock() {
    if (scrollOwners.size > 0) {
      const gap = anchoBarraScroll();
      document.documentElement.setAttribute('scroll-lock', '');
      document.documentElement.style.setProperty('--naia-scrollbar-gap', gap ? `${gap}px` : '0px');
      aplicarCompensacionBarra(gap);
      return;
    }

    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.style.removeProperty('--naia-scrollbar-gap');
    aplicarCompensacionBarra(0);
  }

  function elementosFoco(contenedor) {
    return Array.from(contenedor.querySelectorAll(SELECTORES_FOCO)).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.closest('[hidden]')) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.offsetParent === null && el !== document.activeElement) return false;
      return true;
    });
  }

  global.NaiaUI = {
    bloquearScroll(owner) {
      scrollOwners.add(owner || global);
      syncScrollLock();
    },

    desbloquearScroll(owner) {
      scrollOwners.delete(owner || global);
      syncScrollLock();
    },

    atraparFoco(contenedor) {
      if (!contenedor) return;
      this.liberarFoco(contenedor);

      const foco = elementosFoco(contenedor);
      if (!foco.length) return;

      const primero = foco[0];
      const ultimo = foco[foco.length - 1];
      primero.focus();

      const handler = (evento) => {
        if (evento.key !== 'Tab') return;
        const actuales = elementosFoco(contenedor);
        if (!actuales.length) return;
        const prim = actuales[0];
        const ult = actuales[actuales.length - 1];
        if (evento.shiftKey && document.activeElement === prim) {
          evento.preventDefault();
          ult.focus();
        } else if (!evento.shiftKey && document.activeElement === ult) {
          evento.preventDefault();
          prim.focus();
        }
      };

      contenedor.addEventListener('keydown', handler);
      focoHandlers.set(contenedor, handler);
    },

    liberarFoco(contenedor) {
      if (!contenedor) return;
      const handler = focoHandlers.get(contenedor);
      if (handler) {
        contenedor.removeEventListener('keydown', handler);
        focoHandlers.delete(contenedor);
      }
    },
  };

  function configuracionDescuentoFicticio() {
    const body = document.body;
    const extraCentimos = Number(body?.dataset?.naiaDescuentoExtraCentimos || 2000);
    return {
      activo: body?.dataset?.naiaDescuentoFicticio === 'true',
      extraCentimos: extraCentimos > 0 ? extraCentimos : 2000,
    };
  }

  function compareAtCentimos(priceCentimos, compareAtReal) {
    const { activo, extraCentimos } = configuracionDescuentoFicticio();
    if (activo && priceCentimos > 0) {
      return priceCentimos + extraCentimos;
    }
    if (compareAtReal && compareAtReal > priceCentimos) return compareAtReal;
    return 0;
  }

  function formatearPrecioDesdeEjemplo(centimos, ejemplo) {
    const numero = Number(centimos) / 100;
    const muestra = ejemplo || '';
    const simbolo = muestra.replace(/[\d\s.,]/g, '').trim() || '€';
    const conDecimales = /\.\d{2}|,\d{2}/.test(muestra);
    const valor = conDecimales ? numero.toFixed(2).replace('.', ',') : Math.round(numero).toString();
    if (muestra.indexOf(simbolo) === 0) return `${simbolo}${valor}`;
    return `${valor} ${simbolo}`;
  }

  function aplicarPrecioConCompare(contenedor, priceCentimos, compareAtReal) {
    if (!contenedor) return;
    const actual = contenedor.querySelector('[data-tarjeta-producto-precio-actual], [data-producto-principal-precio-actual]');
    const tachado = contenedor.querySelector('[data-tarjeta-producto-precio-tachado], [data-producto-principal-precio-tachado]');
    if (!actual) return;
    const ejemplo = actual.textContent?.trim();
    actual.textContent = formatearPrecioDesdeEjemplo(priceCentimos, ejemplo);
    const compare = compareAtCentimos(priceCentimos, compareAtReal);
    if (!tachado) return;
    if (compare > priceCentimos) {
      tachado.hidden = false;
      tachado.textContent = formatearPrecioDesdeEjemplo(compare, ejemplo);
    } else {
      tachado.hidden = true;
      tachado.textContent = '';
    }
  }

  global.NaiaPrecio = {
    configuracionDescuentoFicticio,
    compareAtCentimos,
    formatearPrecioDesdeEjemplo,
    aplicarPrecioConCompare,
  };
})(window);
